import { Button, Dropdown, Flex, Skeleton, Space, Tooltip, Tree } from "antd";
import React, { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { TbFilter, TbTimeline } from "react-icons/tb";
import { useToken } from "../../hooks/useToken";
import { TInterest } from "../../models/interests.model";
import { TResponse } from "../../services/apiWorker";
import { getInterests } from "../../services/interests.service";
import {
  followPublicTimeline,
  getFollowingPublicTimelines,
  getPublicTimelines,
  unfollowPublicTimeline,
} from "../../services/ptimelines.service";
import { deepCopy } from "../../utils/deepCopy";
import { CategoryDropdownContent } from "./CategoryDropDown";

interface TimelineD3ChartsActionsProps {
  viewType: "SMALL" | "LARGE";
  setViewType: (viewType: "SMALL" | "LARGE") => void;
  selectedCategoryIds: string[];
  setSelectedCategoryIds: React.Dispatch<React.SetStateAction<string[]>>;
  onClearAllFilters: () => void;
  isAnyFilterActive: boolean;
}

export function TimelineD3ChartsActions({
  viewType,
  setViewType,
  selectedCategoryIds,
  setSelectedCategoryIds,
  onClearAllFilters,
  isAnyFilterActive,
}: TimelineD3ChartsActionsProps) {
  const { token, screens } = useToken();
  const [interests, setInterests] = useState<TResponse<TInterest>>();

  useEffect(() => {
    getInterests({
      onSuccess: (data) => {
        setInterests({ ...data, content: data.content || [] });
      },
      onError: (error) => {
        console.error("Failed to fetch interests:", error);
      },
    });

    const unsubscribe = PubSub.subscribe("categoryDeleted", (_, data) => {
      setInterests((prevState) => {
        if (prevState && prevState.content) {
          const updatedContent = prevState.content.filter(
            (category) => category.id !== data.id
          );
          return { ...prevState, content: updatedContent };
        }

        return prevState;
      });
    });

    return () => {
      PubSub.unsubscribe(unsubscribe);
    };
  }, []);

  const handleAddCategory = (interest: TInterest) => {
    setInterests((prevState) => {
      const copy = deepCopy(prevState);

      if (copy && copy.content) {
        copy.content.push(interest);
      }

      return copy;
    });
  };

  const categoryDropdown = (
    <div
      style={{
        width: "300px",
        maxHeight: "400px",
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadius,
        marginTop: token.margin,
      }}
    >
      <CategoryDropdownContent
        allCategories={interests?.content || []}
        selectedCategories={selectedCategoryIds}
        onChange={setSelectedCategoryIds}
        onAddCategory={handleAddCategory}
        limit={3}
      />
    </div>
  );

  return (
    <Flex
      align="center"
      style={{
        position: screens.md ? "absolute" : "unset",
        right: "0px",
        top: "20px",
        gap: "10px",
        zIndex: 1000,
        borderRadius: token.borderRadius,
        padding: token.paddingSM,
        backgroundColor: token.colorBgBase,
        pointerEvents: "none",
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <Space
        style={{
          pointerEvents: "auto",
        }}
      >
        <Dropdown
          trigger={["click"]}
          dropdownRender={() => (
            <div
              style={{
                width: "300px",
                maxHeight: "400px",
                backgroundColor: token.colorBgContainer,
                borderRadius: token.borderRadius,
                marginTop: token.margin,
              }}
            >
              <DropdownPublicTimelines />
            </div>
          )}
        >
          <Tooltip title="Timelines">
            <Button
              style={{
                border: `1px solid ${token.colorBorderSecondary}`,
                backgroundColor: token.colorBgContainer,
              }}
              type="text"
              size="small"
              icon={
                <TbTimeline
                  style={{ fontSize: "15px", color: token.colorText }}
                />
              }
            />
          </Tooltip>
        </Dropdown>
        <Dropdown trigger={["click"]} dropdownRender={() => categoryDropdown}>
          <Tooltip title="Filter Categories">
            <Button
              style={{
                border: `1px solid ${token.colorBorderSecondary}`,
                backgroundColor: token.colorBgContainer,
              }}
              type="text"
              size="small"
              icon={
                <TbFilter
                  style={{ fontSize: "15px", color: token.colorText }}
                />
              }
            />
          </Tooltip>
        </Dropdown>

        {/* <Tooltip title="Clear All Filters">
            <Button
            type="text"
            size="small"
            icon={
              <TbX
              style={{
                fontSize: "18px",
                color: !isAnyFilterActive
                ? token.colorTextDisabled
                : token.colorText,
              }}
              />
            }
            onClick={onClearAllFilters}
            disabled={!isAnyFilterActive}
            style={{ padding: "0 4px", height: "auto" }}
            />
          </Tooltip> */}
        <Tooltip title="Expand / Collapse Timeline">
          <Button
            type="text"
            size="small"
            style={{
              border: `1px solid ${token.colorBorderSecondary}`,
              backgroundColor: token.colorBgContainer,
            }}
            onClick={() => {
              if (viewType === "SMALL") {
                setViewType("LARGE");
              } else if (viewType === "LARGE") {
                setViewType("SMALL");
              }
            }}
            icon={
              <FaChevronDown
                style={{ fontSize: "10px", color: token.colorText }}
              />
            }
          />
        </Tooltip>
      </Space>
    </Flex>
  );
}

function DropdownPublicTimelines() {
  const [isLoading, setLoading] = useState(true);
  const [publicTimelines, setPublicTimelines] =
    useState<TResponse<{ name: string; id: string }>>(TResponse);
  const [followingTimelines, setFollowingTimelines] =
    useState<TResponse<string>>(TResponse);

  useEffect(() => {
    getPublicTimelines({
      onSuccess: (data) => {
        setPublicTimelines(data);
        setLoading(false);
      },
    });

    getFollowingPublicTimelines({
      onSuccess: setFollowingTimelines,
    });
  }, []);

  return (
    <div
      style={{
        padding: "12px",
        minWidth: "300px",
        width: "100%",
      }}
    >
      {isLoading && <Skeleton active paragraph={true} />}
      <Tree
        checkable
        selectable={false}
        multiple
        checkedKeys={followingTimelines.content}
        onCheck={(items, info) => {
          console.log(info.node.key, info.node.checked);

          if (info.node.checked === false) {
            // is now following

            followPublicTimeline({
              timelineId: info.node.key as string,
              onSuccess: () => {
                console.log("followed", info.node.key);
                setFollowingTimelines((prevState) => {
                  const copy = deepCopy(prevState);
                  copy.content.push(info.node.key as string);
                  return copy;
                });

                PubSub.publish("timelineFollowed", info.node.key);
              },
              onError: (error) => {
                console.error("Failed to follow timeline:", error);
              },
            });
          } else if (info.node.checked === true) {
            // is now unfollowing

            unfollowPublicTimeline({
              timelineId: info.node.key as string,
              onSuccess: () => {
                console.log("unfollowed", info.node.key);
                setFollowingTimelines((prevState) => {
                  const copy = deepCopy(prevState);
                  copy.content = copy.content.filter(
                    (id) => id !== (info.node.key as string)
                  );
                  return copy;
                });

                PubSub.publish("timelineUnfollowed", info.node.key);
              },
              onError: (error) => {
                console.error("Failed to unfollow timeline:", error);
              },
            });
          }

          // for (const item of items as string[]) {
          //   const isNowFollowing = followingTimelines.content.includes(item);
          //   const isNowUnfollowing = !followingTimelines.content.includes(item);
          //   const didntChange = isNowFollowing && isNowUnfollowing;

          //   console.log(isNowFollowing, isNowUnfollowing, didntChange);

          //   if (didntChange) {
          //     continue;
          //   }

          //   if (isNowFollowing) {
          //     // unfollow
          //     unfollowPublicTimeline({
          //       timelineId: item,
          //       onSuccess: () => {
          //         console.log("unfollowed", item);

          //         setFollowingTimelines((prevState) => {
          //           const copy = deepCopy(prevState);
          //           copy.content = copy.content.filter((id) => id !== item);
          //           return copy;
          //         });
          //       },
          //       onError: (error) => {
          //         console.error("Failed to unfollow timeline:", error);
          //       },
          //     });
          //   } else if (isNowUnfollowing) {
          //     // follow
          //     followPublicTimeline({
          //       timelineId: item,
          //       onSuccess: () => {
          //         console.log("followed", item);
          //         setFollowingTimelines((prevState) => {
          //           const copy = deepCopy(prevState);
          //           copy.content.push(item);
          //           return copy;
          //         });
          //       },
          //       onError: (error) => {
          //         console.error("Failed to follow timeline:", error);
          //       },
          //     });
          //   }
          // }
        }}
        treeData={publicTimelines.content.map((timeline) => {
          return {
            key: timeline.id,
            title: timeline.name,
          };
        })}
      />
    </div>
  );
}
